import type { AgentSubscription, AgentSubscriptionPlan, RunCost } from '@/api'

/**
 * A cost in cents as a price. Whole cents to two places; below a cent to the first four places of a dollar, so a
 * cached Haiku step reads `$0.0042` rather than a `$0.00` that looks like nothing ran.
 */
export function formatUsdCost(cents: number | null | undefined): string {
    const value = Number(cents ?? 0)
    if (!Number.isFinite(value) || value <= 0) return '$0.00'
    if (value >= 1) return `$${(value / 100).toFixed(2)}`
    const dollars = value / 100
    if (dollars < 0.0001) return '<$0.0001'
    return `$${dollars.toFixed(4).replace(/0+$/, '')}`
}

/** A token count at a glance: `950`, `12.3k`, `1.2M`. */
export function formatTokens(count: number): string {
    if (count >= 1_000_000) return `${trimmed(count / 1_000_000)}M`
    if (count >= 1_000) return `${trimmed(count / 1_000)}k`
    return String(count)
}

function trimmed(value: number): string {
    return value.toFixed(1).replace(/\.0$/, '')
}

/**
 * How a run's cost breaks down, one clause per line of the tooltip: what was billed per token, what was a share of
 * a subscription seat (with what those calls would have cost on an API key), and the tokens behind it.
 */
export function costBreakdown(cost: RunCost): string[] {
    const lines: string[] = []
    if (cost.meteredCents > 0) lines.push(`API ${formatUsdCost(cost.meteredCents)}`)
    if (cost.subscriptionCents > 0 || cost.listCents > cost.meteredCents) {
        const seatList = cost.listCents - cost.meteredCents
        lines.push(`Subscription ${formatUsdCost(cost.subscriptionCents)} (${formatUsdCost(seatList)} at API prices)`)
    }
    const { input, output, cacheRead, cacheWrite } = cost.tokens
    if (input + output + cacheRead + cacheWrite > 0) {
        const parts = [`${formatTokens(input)} in`, `${formatTokens(output)} out`]
        if (cacheRead > 0) parts.push(`${formatTokens(cacheRead)} cached`)
        lines.push(parts.join(' · '))
    }
    return lines
}

export const SUBSCRIPTION_PLAN_NAMES: Record<AgentSubscriptionPlan, string> = {
    pro: 'Pro',
    max_5x: 'Max 5x',
    // Claude renamed "Max 20x" to "Ultra"; the stored id stays `max_20x` so saved connections keep working.
    max_20x: 'Ultra',
    team: 'Team',
    enterprise: 'Enterprise',
    custom: 'Custom',
    plus: 'Plus',
    chatgpt_pro: 'Pro',
    business: 'Business',
    supergrok: 'SuperGrok',
    supergrok_heavy: 'SuperGrok Heavy',
}

/** A seat's plan as a connection card shows it: `Ultra · $200/mo`, or a prompt when nothing prices it yet. */
export function subscriptionLabel(subscription: AgentSubscription | null): string {
    if (!subscription) return 'Plan unknown — costed at API list price'
    const name = SUBSCRIPTION_PLAN_NAMES[subscription.plan]
    if (subscription.monthlyUsdCents === null) return `${name} · price not set — costed at API list price`
    return `${name} · $${(subscription.monthlyUsdCents / 100).toFixed(0)}/mo`
}
